import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RootState } from "../../../setup";
import { UserRole } from "../../constants/roles";
import { showErrorAlert } from "../../helpers/alert";
import { AdminDiscussionListModel } from "../../hooks/community/models/AdminDiscussionListModel";
import { useCommunityAdminList } from "../../hooks/community/useCommunityAdminList";
import { LoaderOverlay } from "../../hooks/loader/LoaderOverlay";
import { UserModel } from "../../modules/auth/models/UserModel";
import { hasRole } from "../../utils/permissions";
import { CommunityAdminDetails } from "./components/CommunityAdminDetails";
import { CommunityAdminTable } from "./components/CommunityAdminTable";
import { CommunityAdminSection } from "./enums/CommunityAdminSection";
import { useLayout } from "../../../_metronic/layout/core";
import Pagination from "../components/Pagination";

export const CommunityAdminListPage = () => {
    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)
    const user = useSelector<RootState>(({ auth }) => auth.user, shallowEqual) as UserModel
    const history = useHistory();

    useEffect(() => {
        restoreRef.current = config
        setLayout({
            ...config,
            header: { ...config.header, display: true },
            aside: { ...config.aside, display: true },
            toolbar: { ...config.toolbar, display: false },
            footer: { ...config.footer, display: true },
        })

        allowed();

        return () => setLayout(restoreRef.current)
    }, []);

    const allowed = () => {
        const _isAdmin = hasRole(user, UserRole.ADMIN)
        if (!_isAdmin) {
            history.goBack();
            showErrorAlert({ title: 'Access Denied', message: 'You do not have permission to access this page.' });
        }
    }

    const [section, setSection] = useState<CommunityAdminSection>(CommunityAdminSection.Forum);
    const [selected, setSelected] = useState<AdminDiscussionListModel | undefined>(undefined);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const limit = 10;

    const { loading, data, load } = useCommunityAdminList(section, search, currentPage, limit);
    const list = data?.results || [];

    useEffect(() => {
        setCurrentPage(1);
    }, [section, search]);

    useEffect(() => {
        document.body.classList.add('admin-community-container')
        return () => document.body.classList.remove('admin-community-container')
    }, []);

    const totalPages = data ? Math.ceil(data.count / limit) : 1;

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    return (
        <>
            <LoaderOverlay visible={loading} message={'Loading...'} />
            {selected &&
                <CommunityAdminDetails
                    onReloadList={load}
                    detail={selected}
                    onClose={() => setSelected(undefined)}
                />
            }
            <div className="admin-community__wrapper">
                <h3>Community</h3>

                <div className="admin-community__selector">
                    <button className={clsx({ "active": section === CommunityAdminSection.Forum })}
                        onClick={() => setSection(CommunityAdminSection.Forum)}
                    >
                        Forum
                    </button>
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="1" height="20" viewBox="0 0 1 20" fill="none">
                            <path d="M0.5 0V20" stroke="#B4B4B4" />
                        </svg>
                    </div>
                    <button className={clsx({ "active": section === CommunityAdminSection.Mastermind })}
                        onClick={() => setSection(CommunityAdminSection.Mastermind)}
                    >
                        Mastermind Cirlces
                    </button>

                    <button className="create_btn" onClick={() => history.push('/admin/community/new')}>
                        Start Forum
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                            <path d="M8.5 0.379395L12 3.38354H0" stroke="white" />
                        </svg>
                    </button>
                </div>

                <div className="admin-community__list_wrap">
                    <div className="search_input">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="#C0C0C0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M17.5 17.5L13.875 13.875" stroke="#C0C0C0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <input type="text" placeholder="Search Forum" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>

                    <CommunityAdminTable
                        list={list}
                        onSelect={(e) => setSelected(e)}
                    />
                    {list.length > 0 && data && (
                        <div className="tap-flex-center mt-5">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}